import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, AppBar, Toolbar, Typography, List, ListItem,
  ListItemIcon, ListItemText, IconButton, Divider, Avatar,
  Collapse, ListItemButton, useMediaQuery, useTheme
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  ShoppingCart as OrdersIcon,
  Assessment as AnalyticsIcon,
  Settings as SettingsIcon,
  Payment as PaymentIcon,
  LocalShipping as ShippingIcon,
  Store as StoreIcon,
  Logout as LogoutIcon,
  ExpandLess,
  ExpandMore,
  Inventory as ProductIcon,
  People as CustomerIcon,
  Receipt as InvoiceIcon,
  Campaign as MarketingIcon,
  Home as HomeIcon,
  Notifications as NotificationsIcon,
  Category as CategoryIcon
} from '@mui/icons-material';

const drawerWidth = 260;

const menuItems = [
  {
    title: '儀表板',
    icon: <DashboardIcon />,
    path: '/admin/dashboard'
  },
  { divider: true, label: '商品' },
  {
    title: '商品管理',
    icon: <ProductIcon />,
    path: '/admin/products'
  },
  {
    title: '分類管理',
    icon: <CategoryIcon />,
    path: '/admin/categories'
  },
  {
    title: '訂單管理',
    icon: <OrdersIcon />,
    path: '/admin/orders'
  },
  {
    title: '會員管理',
    icon: <CustomerIcon />,
    path: '/admin/members'
  },
  { divider: true, label: '報表與行銷' },
  {
    title: '數據分析',
    icon: <AnalyticsIcon />,
    path: '/admin/analytics'
  },
  {
    title: '再行銷系統',
    icon: <MarketingIcon />,
    path: '/admin/remarketing'
  },
  { divider: true, label: '設定' },
  {
    title: '金流設定',
    icon: <PaymentIcon />,
    children: [
      { title: '付款方式', path: '/admin/settings', tab: 0 },
      { title: 'SmilePay 金物流', path: '/admin/settings', tab: 1 },
      { title: '聯合信用卡', path: '/admin/settings', tab: 2 },
      { title: 'LINE Pay', path: '/admin/settings', tab: 3 },
      { title: '運費設定', path: '/admin/settings', tab: 4 }
    ]
  },
  {
    title: '行銷追蹤',
    icon: <MarketingIcon />,
    children: [
      { title: '追蹤碼設定', path: '/admin/settings', tab: 5 },
      { title: '登入方式', path: '/admin/settings', tab: 6 }
    ]
  },
  {
    title: '商店設定',
    icon: <StoreIcon />,
    children: [
      { title: '商店資訊', path: '/admin/settings', tab: 7 },
      { title: 'SEO 設定', path: '/admin/settings', tab: 8 },
      { title: '首頁特色', path: '/admin/settings', tab: 9 }
    ]
  },
  {
    title: '通知中心',
    icon: <NotificationsIcon />,
    path: '/admin/notifications'
  }
];

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState({ '金流設定': false, '行銷追蹤': false, '商店設定': false });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (item) => {
    if (item.children) {
      setOpenMenus(prev => ({
        ...prev,
        [item.title]: !prev[item.title]
      }));
    } else {
      navigate(item.path);
      if (isMobile) setMobileOpen(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const isActive = (path) => location.pathname === path;

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo / Header */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 45, height: 45 }}>
          <StoreIcon />
        </Avatar>
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '0.95rem' }}>
            優爾網路商城
          </Typography>
          <Typography variant="caption" color="text.secondary">
            管理系統
          </Typography>
        </Box>
      </Box>

      <Divider />

      {/* Menu Items */}
      <List sx={{ flex: 1, px: 1 }}>
        {menuItems.map((item, index) => {
          if (item.divider) {
            return (
              <Box key={index}>
                <Divider sx={{ my: 1 }} />
                {item.label && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ px: 2, py: 0.5, display: 'block', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}
                  >
                    {item.label}
                  </Typography>
                )}
              </Box>
            );
          }

          if (item.children) {
            return (
              <React.Fragment key={item.title}>
                <ListItemButton
                  onClick={() => handleMenuClick(item)}
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.title} />
                  {openMenus[item.title] ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={openMenus[item.title]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children.map((child) => (
                      <ListItemButton
                        key={child.title}
                        onClick={() => {
                          navigate(child.path, { state: { tab: child.tab } });
                          if (isMobile) setMobileOpen(false);
                        }}
                        sx={{
                          pl: 6,
                          borderRadius: 1,
                          mb: 0.5,
                          bgcolor: isActive(child.path) ? 'action.selected' : 'transparent'
                        }}
                      >
                        <ListItemText
                          primary={child.title}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          }

          return (
            <ListItemButton
              key={item.title}
              onClick={() => handleMenuClick(item)}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                bgcolor: isActive(item.path) ? 'primary.main' : 'transparent',
                color: isActive(item.path) ? 'white' : 'inherit',
                '&:hover': {
                  bgcolor: isActive(item.path) ? 'primary.dark' : 'action.hover'
                }
              }}
            >
              <ListItemIcon sx={{
                minWidth: 40,
                color: isActive(item.path) ? 'white' : 'inherit'
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.title} />
            </ListItemButton>
          );
        })}
      </List>

      <Divider />

      {/* Footer Actions */}
      <List sx={{ px: 1 }}>
        <ListItemButton
          onClick={() => window.open('/', '_blank')}
          sx={{ borderRadius: 1, mb: 0.5 }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}><HomeIcon /></ListItemIcon>
          <ListItemText primary="前往前台" />
        </ListItemButton>
        <ListItemButton
          onClick={handleLogout}
          sx={{ borderRadius: 1, color: 'error.main' }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: 'error.main' }}><LogoutIcon /></ListItemIcon>
          <ListItemText primary="登出" />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      {/* AppBar for mobile */}
      <AppBar
        position="fixed"
        sx={{
          display: { md: 'none' },
          bgcolor: 'white',
          color: 'text.primary',
          boxShadow: 1
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap>
            優爾網路商城管理系統
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth
            }
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: '1px solid #e0e0e0'
            }
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: 8, md: 0 },
          minHeight: '100vh'
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default AdminLayout;
